// CreateAccountPage.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Button,
  TextInput,
  Form,
  FormGroup,
  ProgressIndicator,
  ProgressStep,
  InlineLoading,
  Accordion,
  AccordionItem,
} from '@carbon/react';
import BillingComponent from './BillingComponent';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ChartMultitype,
  Integration,
  TaskStar,
  Notification,
} from '@carbon/icons-react';
import amplitude from './../../analytics/amplitude'; // ✅ add tracking
import { sendVerificationWithPoW } from '../../utils/emailVerification';

const CreateAccountPage = ({ token, setToken }) => {
  // ────────────────────────────────────────────────────────────────
  // Logging system
  // ────────────────────────────────────────────────────────────────
  const LOG_LEVELS = {
    ASSERT: 1,
    ERROR: 2,
    WARN: 3,
    INFO: 4,
    DEBUG: 5,
    TRACE: 6,
  };
  const log = {
    a: () => {},
    e: () => {},
    w: () => {},
    i: () => {},
    d: () => {},
    t: () => {},
    level: LOG_LEVELS.DEBUG,
  };
  (function setLogLevel(level) {
    if (level >= LOG_LEVELS.ASSERT) log.a = console.assert.bind(console);
    if (level >= LOG_LEVELS.ERROR) log.e = console.error.bind(console);
    if (level >= LOG_LEVELS.WARN) log.w = console.warn.bind(console);
    if (level >= LOG_LEVELS.INFO) log.i = console.info.bind(console);
    if (level >= LOG_LEVELS.DEBUG) log.d = console.debug.bind(console);
    if (level >= LOG_LEVELS.TRACE) log.t = console.log.bind(console);
  })(log.level);

  const PLAN_PRICE_USD = 15;
  const TRIAL_DAYS = 7;
  const FLOW = 'signup';
  const PAGE = 'CreateAccountPage';

  // Safe wrapper around amplitude.track
  const track = (eventName, props = {}) => {
    try {
      if (amplitude && typeof amplitude.track === 'function') {
        amplitude.track(eventName, props);
      } else {
        log.d(`[amp-skip] ${eventName}`, props);
      }
    } catch (e) {
      log.d(`[amp-error] ${eventName}`, e);
    }
  };

  // Build shared props
  const defaultProps = (extra = {}) => ({
    page: PAGE,
    flow: FLOW,
    plan: 'monthly_15_usd',
    trial_days: TRIAL_DAYS,
    ...utmRef.current,
    ...extra,
  });

  // Parse UTM/referrer once
  const location = useLocation();
  const utmRef = useRef({});
  useEffect(() => {
    const sp = new URLSearchParams(location.search || '');
    utmRef.current = {
      utm_source: sp.get('utm_source') || undefined,
      utm_medium: sp.get('utm_medium') || undefined,
      utm_campaign: sp.get('utm_campaign') || undefined,
      utm_term: sp.get('utm_term') || undefined,
      utm_content: sp.get('utm_content') || undefined,
      referral: sp.get('ref') || undefined,
      document_referrer: document.referrer || undefined,
    };
  }, [location.search]);

  const navigate = useNavigate();
  const { initialStep, token: socialToken, email } = location.state || {};

  const [currentStep, setCurrentStep] = useState(0);
  const [userData, setUserData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [authToken, setAuthToken] = useState(null);
  const [createdUser, setCreatedUser] = useState(null);
  const [cardholderName, setCardholderName] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [loadingMessage, setLoadingMessage] = useState('');

  const steps = ['Create User', 'Verify Email', 'Billing Info'];
  const stepName = idx => steps[idx] || `Step ${idx}`;

  // If coming from social-login, jump to billing
  useEffect(() => {
    if (initialStep === 2 && socialToken && email) {
      setCurrentStep(2);
      setAuthToken(socialToken);
      setCreatedUser(email);
      track('Signup Viewed', defaultProps({ source: 'social' }));
      track(
        'Signup Step Viewed',
        defaultProps({ step_index: 2, step_name: stepName(2) })
      );
      track('Billing Viewed', defaultProps({ source: 'social' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialStep, socialToken, email]);

  // Initial page view (native path)
  useEffect(() => {
    if (!(initialStep === 2 && socialToken && email)) {
      track('Signup Viewed', defaultProps({ source: 'native' }));
      track(
        'Signup Step Viewed',
        defaultProps({
          step_index: currentStep,
          step_name: stepName(currentStep),
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track step changes
  useEffect(() => {
    track(
      'Signup Step Viewed',
      defaultProps({
        step_index: currentStep,
        step_name: stepName(currentStep),
      })
    );
    if (currentStep === 2) {
      track('Billing Viewed', defaultProps());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  // Clear the verification code each time the user lands on the “Verify Email” step
  useEffect(() => {
    if (currentStep === 1) {
      setVerificationCode('');
    }
  }, [currentStep]);

  // Native login helper
  const loginWithUsernameAndPassword = async (uname, pw) => {
    try {
      track('Login Attempted', defaultProps({ method: 'native' }));
      const resp = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          id: uname,
          email: uname,
          authType: 'native',
          password: pw,
        }),
      });
      if (!resp.ok) {
        track(
          'Login Failed',
          defaultProps({ reason: 'http_error', status: resp.status })
        );
        throw new Error('Login failed');
      }
      const data = await resp.json();
      track('Login Succeeded', defaultProps());
      return data.data.tokens.access_token;
    } catch (err) {
      log.w(`Login error: ${err}`);
      return null;
    }
  };

  // ────────────────────────────────────────────────────────────────
  // Membership features (for the accordion)
  // ────────────────────────────────────────────────────────────────
  const membershipFeatures = [
    {
      title: 'Multi-Model AI Access',
      icon: ChartMultitype,
      items: [
        'Chat with ChatGPT, Claude, Gemini, DeepSeek & more',
        'Compare responses across models',
        'Switch models mid-conversation',
      ],
    },
    {
      title: 'Advanced Tools & Integrations',
      icon: Integration,
      items: [
        '15+ search engines (Google, Reddit, PubMed…)',
        'Integrate Google Drive, S3, Evernote',
        'Full GitHub file operations',
        'Email/SMS alerts & attachments',
      ],
    },
    {
      title: 'Smart Task Orchestrator',
      icon: TaskStar,
      items: [
        'Build complex multi-step tasks',
        'Automated data aggregation',
        'Market monitoring & financial insights',
        'Content-generation workflows',
      ],
    },
    {
      title: 'Automations & Insights',
      icon: Notification,
      items: [
        'Schedule hourly/daily tasks',
        'AI-curated daily insights',
        'Automated report generation',
        'Text/email alerts on your topics',
      ],
    },
  ];

  // ────────────────────────────────────────────────────────────────
  // Step 1: Create account (send code)
  // ────────────────────────────────────────────────────────────────
  const handleCreateAccount = async e => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');
    setLoadingMessage('');

    const { firstName, lastName, email, password, confirmPassword } = userData;
    if (!firstName || !lastName || !email || !password || !confirmPassword) {
      setErrorMessage('All fields are required');
      setLoading(false);
      return;
    }
    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match');
      setLoading(false);
      return;
    }

    track('Signup Step Submitted', defaultProps({ step_index: 0 }));

    try {
      const resp = await sendVerificationWithPoW(email, companyWebsite, {
        onStatusChange: msg => setLoadingMessage(msg),
      });
      if (!resp.ok) {
        track(
          'Email Verification Code Send Failed',
          defaultProps({ step_index: 0, status: resp.status })
        );
        throw new Error('Failed to send verification code');
      }
      track('Email Verification Code Sent', defaultProps({ step_index: 0 }));
      track(
        'Signup Step Completed',
        defaultProps({ step_index: 0, step_name: stepName(0) })
      );
      setCurrentStep(1);
    } catch (err) {
      setErrorMessage(err.message || 'Verification start failed');
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  // ────────────────────────────────────────────────────────────────
  // Step 2: Verify code & create user
  // ────────────────────────────────────────────────────────────────
  const handleVerifyCode = async e => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');

    track('Signup Step Submitted', defaultProps({ step_index: 1 }));

    try {
      const verifyResp = await fetch('/mlapi/auth/validate-email-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: userData.email, code: verificationCode }),
      });
      if (!verifyResp.ok) {
        track(
          'Email Verification Failed',
          defaultProps({ step_index: 1, status: verifyResp.status })
        );
        throw new Error('Invalid verification code');
      }
      track(
        'Email Verification Code Validated',
        defaultProps({ step_index: 1 })
      );

      const createResp = await fetch('/mlapi/auth/create-user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData),
      });
      const createData = await createResp.json();
      if (!createResp.ok) {
        track(
          'User Account Create Failed',
          defaultProps({ step_index: 1, status: createResp.status })
        );
        throw new Error(createData.message);
      }
      track('User Account Created', defaultProps({ step_index: 1 }));

      setCreatedUser(userData.email);
      const tok = await loginWithUsernameAndPassword(
        userData.email,
        userData.password
      );
      setAuthToken(tok);

      track(
        'Signup Step Completed',
        defaultProps({ step_index: 1, step_name: stepName(1) })
      );
      setCurrentStep(2);
    } catch (err) {
      setErrorMessage(err.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  // Parent-side handlers around Billing actions so we can track Back/Success
  const handleBillingBack = () => {
    track('Billing Cancelled', defaultProps({ reason: 'user_back' }));
    setCurrentStep(1);
  };

  const handleBillingSuccess = () => {
    // Child already tracks "Subscription Created"; we finalize the funnel here.
    track('Signup Completed', defaultProps({ result: 'success' }));
    setToken(authToken, createdUser, createdUser);
  };

  // UI helpers
  const renderMessages = () => (
    <>
      {errorMessage && <div className="error-message">{errorMessage}</div>}
      {successMessage && (
        <div className="success-message">{successMessage}</div>
      )}
    </>
  );

  // ────────────────────────────────────────────────────────────────
  // Render each step
  // ────────────────────────────────────────────────────────────────
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <Form onSubmit={handleCreateAccount}>
            <FormGroup legendText="Account Details">
              <TextInput
                id="firstName"
                labelText="First Name"
                placeholder="Enter your first name"
                value={userData.firstName}
                onChange={e =>
                  setUserData({ ...userData, firstName: e.target.value })
                }
              />
              <TextInput
                id="lastName"
                labelText="Last Name"
                placeholder="Enter your last name"
                value={userData.lastName}
                onChange={e =>
                  setUserData({ ...userData, lastName: e.target.value })
                }
              />
              <TextInput
                id="email"
                labelText="Email"
                type="email"
                placeholder="Enter your email"
                value={userData.email}
                onChange={e =>
                  setUserData({ ...userData, email: e.target.value })
                }
              />
              <TextInput
                id="password"
                labelText="Password"
                type="password"
                placeholder="Enter your password"
                value={userData.password}
                onChange={e =>
                  setUserData({ ...userData, password: e.target.value })
                }
              />
              <TextInput
                id="confirmPassword"
                labelText="Confirm Password"
                type="password"
                placeholder="Re-enter your password"
                value={userData.confirmPassword}
                onChange={e =>
                  setUserData({ ...userData, confirmPassword: e.target.value })
                }
              />
            </FormGroup>
            {/* Honeypot field — positioned offscreen, invisible to humans */}
            <div
              style={{
                position: 'absolute',
                left: '-9999px',
                top: '-9999px',
                width: '1px',
                height: '1px',
                overflow: 'hidden',
              }}
              aria-hidden="true">
              <label htmlFor="companyWebsite">Company Website</label>
              <input
                type="text"
                id="companyWebsite"
                name="companyWebsite"
                tabIndex={-1}
                autoComplete="off"
                value={companyWebsite}
                onChange={e => setCompanyWebsite(e.target.value)}
              />
            </div>
            {renderMessages()}
            <div className="action-buttons">
              <Button
                kind="secondary"
                onClick={() => {
                  track('Signup Cancelled', defaultProps({ step_index: 0 }));
                  navigate('/');
                }}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? (
                  <InlineLoading description={loadingMessage || undefined} />
                ) : (
                  'Continue to Verification'
                )}
              </Button>
            </div>
          </Form>
        );

      case 1:
        return (
          <Form onSubmit={handleVerifyCode}>
            <FormGroup legendText="Email Verification">
              <p>We've sent a 6-digit code to {userData.email}</p>
              <TextInput
                id="verificationCode"
                labelText="Verification Code"
                placeholder="Enter 6-digit code"
                value={verificationCode}
                onChange={e => setVerificationCode(e.target.value)}
              />
            </FormGroup>
            {renderMessages()}
            <div className="action-buttons">
              <Button
                kind="secondary"
                onClick={() => {
                  track(
                    'Signup Step Back',
                    defaultProps({ from_step: 1, to_step: 0 })
                  );
                  setCurrentStep(0);
                }}>
                Back
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? <InlineLoading /> : 'Verify and Continue'}
              </Button>
            </div>
          </Form>
        );

      case 2:
        return (
          <div className="billing-container">
            {/* Collapsible “Why join?” */}
            <div className="membership-details">
              <Accordion>
                <AccordionItem title="Why join Fresh Focus AI?">
                  <ul>
                    {membershipFeatures.map((sec, i) => (
                      <li key={i}>
                        <sec.icon size={16} /> <strong>{sec.title}</strong>
                        <ul>
                          {sec.items.map((it, j) => (
                            <li key={j}>{it}</li>
                          ))}
                        </ul>
                      </li>
                    ))}
                  </ul>
                </AccordionItem>
              </Accordion>
            </div>

            {/* Pricing & auto-renew notice */}
            <p className="billing-instructions">
              Start your Fresh Focus AI membership subscription for{' '}
              <strong>$15/month</strong> (auto-renewing monthly). Enjoy a{' '}
              <strong>7-day free trial</strong>, and cancel anytime.
            </p>

            <div className="billing-form-wrapper">
              <BillingComponent
                token={authToken}
                userEmail={createdUser}
                cardholderName={cardholderName}
                setCardholderName={setCardholderName}
                onSuccess={handleBillingSuccess}
                onCancel={handleBillingBack}
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="create-account-page">
      <div className="create-account-container">
        <ProgressIndicator currentIndex={currentStep}>
          {steps.map(s => (
            <ProgressStep label={s} key={s} />
          ))}
        </ProgressIndicator>
        <div className="step-content">
          <h3>{steps[currentStep]}</h3>
          {renderStepContent()}
        </div>
      </div>
    </div>
  );
};

export default CreateAccountPage;
